import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { from, of, switchMap } from 'rxjs';
import { AppSettingService } from './app-setting.service';

const INVALID_BASE_PATH_PREFIX = '<not set>';

function isHttpUrl(url: string): boolean {
    return /^https?:\/\//i.test(url);
}

function combineBaseUrl(baseUrl: string, path: string): string {
    const normalizedBase = baseUrl.replace(/\/+$/, '');
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    return `${normalizedBase}${normalizedPath}`;
}

function normalizeApiRequestUrl(currentUrl: string, apiBaseUrl: string): string {
    if (!apiBaseUrl) {
        return currentUrl;
    }

    if (currentUrl.startsWith(INVALID_BASE_PATH_PREFIX)) {
        const suffix = currentUrl.slice(INVALID_BASE_PATH_PREFIX.length);
        return combineBaseUrl(apiBaseUrl, suffix);
    }

    if (!isHttpUrl(currentUrl) && (currentUrl.startsWith('/api/') || currentUrl.startsWith('api/'))) {
        return combineBaseUrl(apiBaseUrl, currentUrl);
    }

    return currentUrl;
}

export const apiUrlInterceptor: HttpInterceptorFn = (req, next) => {
    const translocoService = inject(TranslocoService);
    const appSettingService = inject(AppSettingService);

    // Check if we have a stored language preference
    const storedLang = localStorage.getItem('userLanguage');

    // Use the stored language or fallback to the active language or default
    const lang = storedLang || translocoService.getActiveLang() || 'fr';

    const shouldResolveApiBasePath =
        req.url.startsWith(INVALID_BASE_PATH_PREFIX) ||
        (!isHttpUrl(req.url) && (req.url.startsWith('/api/') || req.url.startsWith('api/')));

    if (shouldResolveApiBasePath) {
        const config$ = appSettingService.isConfigLoaded()
            ? of(appSettingService.currentConfig)
            : from(appSettingService.ensureConfigLoaded().then(config => config).catch(() => null));

        return config$.pipe(
            switchMap(config => {
                const url = normalizeApiRequestUrl(req.url, config?.apiBaseUrl ?? appSettingService.getApiUrl());

                const clonedReq = req.clone({
                    url,
                    withCredentials: true,
                    setHeaders: {
                        'Accept-Language': lang
                    },
                });

                return next(clonedReq);
            })
        );
    }

    // Clone the request with the language header
    const clonedReq = req.clone({
        withCredentials: true,
        setHeaders: {
            'Accept-Language': lang
        },
    });

    return next(clonedReq);
};